import { useState, useEffect } from 'react';
import { Event } from '@/types/event/event';
import { fetchOrganizationEvents } from '@/services/event/organizationEvents';
import { getPricesForEvent } from '@/services/ticket/getPricesForEvent';
import { getTicketsAvailableForPrices } from '@/services/ticket/getTicketsAvailable';

interface EventTicketsAvailable {
  [key: string]: number;
}

export const useEventsOnDay = (date: string | undefined, tenantId: string | null) => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [eventTicketsAvailable, setEventTicketsAvailable] = useState<EventTicketsAvailable>({});

  useEffect(() => {
    if (!date || !tenantId) {
      setError('No date or organization provided');
      setLoading(false);
      return;
    }

    const fetchEvents = async (): Promise<void> => {
      try {
        const data = await fetchOrganizationEvents(tenantId);
        
        // Filter events for the selected date
        const eventsOnDate = data.filter((event: Event) => {
          const eventDate = event.date.split('T')[0];
          return eventDate === date;
        });
        
        // Sort events by start time
        const sortedEvents = eventsOnDate.sort((a: Event, b: Event) => {
          const timeA = a.time_start.split(':').map(Number);
          const timeB = b.time_start.split(':').map(Number);
          
          if (timeA[0] !== timeB[0]) {
            return timeA[0] - timeB[0];
          }
          return timeA[1] - timeB[1];
        });
        
        setEvents(sortedEvents);

        // Fetch prices and tickets available for each event
        const eventDetailsPromises = sortedEvents.map(async (event: Event) => {
          try {
            const pricesData = await getPricesForEvent(event.uuid);
            const pricesWithTickets = await getTicketsAvailableForPrices(pricesData);
            
            const totalTicketsAvailable = pricesWithTickets.reduce(
              (sum, price) => sum + (price.tickets_available || 0), 
              0
            );
            
            return { [event.uuid]: totalTicketsAvailable };
          } catch (error) {
            console.error('Error fetching event details:', error);
            return { [event.uuid]: 0 };
          }
        });

        const eventDetailsResults = await Promise.all(eventDetailsPromises);
        const ticketsAvailableMap = eventDetailsResults.reduce((acc, curr) => ({
          ...acc,
          ...curr
        }), {});
        
        setEventTicketsAvailable(ticketsAvailableMap);
        setTimeout(() => setLoading(false), 1000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    fetchEvents();
  }, [date, tenantId]);

  return { events, loading, error, eventTicketsAvailable };
}; 